// src/services/ProductService.js
import { api } from '../utils/api'
import { get as getIdb, set as setIdb } from 'idb-keyval'
import type { Product, ProductAdditionsBlock, ProductExceptionsBlock } from '../types/@types/product'
import type { Category } from '../types/@types/category'
import { buildProductFormData, type ProductFormValues } from '../utils/formData'

export interface ProductApi {
  id: number
  name: string
  description: string
  price: number
  base_price?: number
  category: string | Category
  image_path?: string
  imageUrl?: string
  active: boolean
  ingredients: string[];
  maxFlavors?: number
  additions?: ProductAdditionsBlock
  exceptions?: ProductExceptionsBlock
}

export function mapProduct(apiProduct: any): Product {
  const {
    id,
    image_path,
    imagePath,
    imageUrl,
    maxFlavors,
    category,
    additions,
    exceptions,
    base_price,
    ...rest
  } = apiProduct

  const resolvedImagePath = imagePath || image_path || (imageUrl ? imageUrl.replace('uploads/', '') : undefined);

  const mappedCategory =
    typeof category === 'string'
      ? category
      : {
        id: (category as any).id,
        name: (category as any).name,
        icon: (category as any).icon,
        createdAt: (category as any).createdAt,
      }

  const mapAdditions = (raw: any): ProductAdditionsBlock => {
    if (!raw) return { enabled: false, options: [] }
    const options = Array.isArray(raw) ? raw : (raw.options || [])
    return {
      enabled: options.length > 0,
      options: options.map((opt: any) => ({
        key: opt.key,
        name: opt.name,
        priceDelta: Number(opt.priceDelta ?? opt.price_delta ?? 0),
        maxQty: Number(opt.maxQty ?? opt.max_qty ?? 1),
        active: Boolean(opt.active ?? true),
      })),
    }
  }

  const mapExceptions = (raw: any): ProductExceptionsBlock => {
    if (!raw) return { enabled: false, options: [] }
    const options = Array.isArray(raw) ? raw : (raw.options || [])
    return {
      enabled: options.length > 0,
      options: options.map((opt: any) => ({
        key: opt.key,
        name: opt.name,
        affectsPrice: Boolean(opt.affectsPrice ?? opt.affects_price ?? false),
        priceDelta: Number(opt.priceDelta ?? opt.price_delta ?? 0),
        active: Boolean(opt.active ?? true),
      })),
    }
  }

  return {
    id,
    image_path: resolvedImagePath,
    imageUrl: imageUrl || (resolvedImagePath ? `uploads/${resolvedImagePath}` : undefined),
    maxFlavors,
    category: mappedCategory,
    additions: mapAdditions(additions),
    exceptions: mapExceptions(exceptions),
    base_price: base_price ?? rest.price,
    ...rest,
  }
}

export const getProducts = async (): Promise<Product[]> => {
  try {
    const { data } = await api.get<ProductApi[]>('/products')
    const mapped = data.map(mapProduct)
    setIdb('DufiWaiter_CachedProducts', mapped).catch(() => {})
    return mapped
  } catch (err) {
    const cached = await getIdb<Product[]>('DufiWaiter_CachedProducts')
    if (cached && cached.length > 0) {
      return cached
    }
    throw err
  }
}

export const getProductById = async (id: string): Promise<Product> => {
  const { data } = await api.get<ProductApi>(`/products/${id}`)
  return mapProduct(data)
}

export const getCategories = async () => {
  try {
    const { data } = await api.get('/categories')
    setIdb('DufiWaiter_CachedCategories', data).catch(() => {})
    return data
  } catch (err) {
    const cached = await getIdb<any[]>('DufiWaiter_CachedCategories')
    if (cached && cached.length > 0) {
      return cached
    }
    throw err
  }
}

export const createProduct = async (form: ProductFormValues): Promise<Product> => {
  if (form.image) {
    const fd = buildProductFormData(form)
    const { data } = await api.post<ProductApi>('/products', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return mapProduct(data)
  }

  const { image, ...rest } = form
  const payload = {
    ...rest,
    ingredients: form.ingredients.map(i => i.name),
    maxFlavors: form.maxFlavors ?? 0,
    additions: form.additions ?? { enabled: false, options: [] },
    exceptions: form.exceptions ?? { enabled: false, options: [] },
  }
  const { data } = await api.post<ProductApi>('/products', payload)
  return mapProduct(data)
}

export const updateProduct = async (
  id: string,
  form: ProductFormValues,
): Promise<Product> => {
  if (form.image) {
    const fd = buildProductFormData(form)
    const { data } = await api.put<ProductApi>(`/products/${id}`, fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return mapProduct(data)
  }

  const { image, ...rest } = form
  const payload = {
    ...rest,
    ingredients: form.ingredients.map(i => i.name),
    maxFlavors: form.maxFlavors ?? 0,
    additions: form.additions ?? { enabled: false, options: [] },
    exceptions: form.exceptions ?? { enabled: false, options: [] },
  }
  const { data } = await api.put<ProductApi>(`/products/${id}`, payload)
  return mapProduct(data)
}

export const deleteProduct = async (id: string): Promise<void> => {
  await api.delete(`/products/${id}`)
}
